/**
 * Planning Phase 3 (plan P3-3b): the workflow dialogs and panels of the case page, the wording of the Phase 3
 * refusals and the names of the Phase 3 journal types. Spread into en.ts.
 */
export default {
  workflow: {
    dialog: {
      confirm: 'Confirm',
      cancel: 'Cancel',
      optional: 'optional',
      required: 'Required.',
      choose: 'Choose…',
      yes: 'Yes',
      no: 'No',
      notRecorded: 'Not recorded',
      days: 'days',
      effectiveHint: 'The date this takes effect: today, or at most 30 days back.',
      numberHint:
        'Proposed from the numbering format. Change it before you confirm if the letter carries another number.',
      listMissing: 'This list is not available yet.',
      invalid: 'Some values were refused:',
      reason: 'Reason',
      agencies: 'Agencies to consult',
      agenciesHint: 'One referral goes to each agency, with the comment window of this circulation.',
      agenciesRequired: 'Choose at least one agency.',
      agencyOpen: 'already consulted',
      drawing: 'Drawing',
      drawingSame: 'Use the drawing already on the case',
      drawingSameHint: 'The latest drawing is checked again as the next revision.',
      drawingReplace: 'Upload the amended drawing',
      drawingReplaceHint: 'The new drawing becomes the next revision.',
      drawingNoPrevious: 'There is no earlier drawing to reuse, so upload one.',
      uploading: 'Uploading the drawing… {{percent}}%',
      finalizing: 'Checking the upload…',
      submitting: 'Saving…',
    },
    kinds: { internal: 'Council department', external: 'Outside agency' },
    conditions: {
      add: 'Add a condition',
      fromLibrary: 'Add from the library',
      chooseTemplate: 'Choose a library condition…',
      pCode: 'Compliance stage',
      text: 'Condition',
      remove: 'Remove',
      none: 'No conditions yet.',
      max: 'At most {{max}} conditions.',
      code: 'Library code {{code}}',
      noOpen: 'Every condition has been complied with.',
      digestOnly:
        "{{number}} of the case's conditions came from another system without their text and cannot be shown here. Confirming replaces the case's conditions with the list below, so add them again from the source letter first.",
    },
    decision: {
      title: 'Decision and conditions',
      number: 'Decision number {{number}}, issued {{date}}',
      noNumber: 'No decision number has been issued yet.',
      conditions: 'Conditions',
      noConditions: 'No conditions are recorded.',
      textAbsent: 'The text of this condition is held by the system that sent the case.',
      complied: 'Complied',
      open: 'Not yet complied',
      preGrant: 'Before the grant',
    },
    flags: { title: 'Recorded facts', counters: 'Counts' },
    referrals: {
      title: 'Referrals',
      round: 'Round {{round}}',
      requested: 'Requested {{date}}',
      due: 'Comments due {{date}}',
      overdue: 'Window passed',
      late: 'Late answer',
      answered: 'Answered {{date}}',
      status: { open: 'Waiting for comments', answered: 'Answered', withdrawn: 'Withdrawn' },
      none: 'No referrals yet.',
      request: 'Request a referral',
      requestTitle: 'Request a referral',
      requestHint: 'Each agency gets the comment window of the latest circulation.',
      answer: 'Record the answer',
      answerTitle: 'Answer from {{agency}}',
      outcome: 'Answer',
      answeredOn: 'Date of the answer',
      deemedHint:
        'The comment window has passed. Record a deemed answer only after checking that OSC 3.0 Plus holds no reply.',
      conditionsRequired: 'Requirements need at least one condition.',
      withdraw: 'Withdraw',
      withdrawTitle: 'Withdraw the referral to {{agency}}',
      withdrawBody: 'The agency is no longer asked to comment. An answer it already sent stays on the case.',
      outcomes: { requirements: 'Requirements', no_requirements: 'No requirements', deemed: 'Deemed: no reply' },
      departmentNote: 'Only the referrals to your department are shown.',
      letterHint:
        "File the agency's letter under Other documents on this case: the answer here records only its outcome and conditions.",
    },
    links: {
      title: 'Linked cases',
      none: 'No linked case.',
      relations: { km: 'Planning permission (KM)' },
      kinds: { case: 'A case in this system', reference: 'An outside reference', exemption: 'An exemption' },
      parentAbsent: 'The linked case is not held in this installation.',
      reference: 'Reference {{reference}}',
      validity: 'Issued {{issued}}, valid until {{until}}',
      link: 'Link {{relation}}',
      linkTitle: 'Link: {{relation}}',
      unlink: 'Unlink',
      unlinkTitle: 'Unlink {{relation}}',
      unlinkBody: 'The link is removed from this case. The other case is not changed.',
      target: 'Link to',
      searchLabel: 'Find a case',
      searchPlaceholder: 'Reference or project name',
      search: 'Search',
      noResults: 'No case matches.',
      chosen: 'Chosen: {{caseRef}}',
      referenceLabel: 'Reference',
      issuedOn: 'Issued on',
      validUntil: 'Valid until',
      chooseCase: 'Choose a case first.',
    },
    documents: {
      title: 'Other documents',
      none: 'No other documents yet.',
      revision: 'Revision {{revision}}',
      shared: 'Shown to the applicant',
      upload: 'Upload a document',
      uploadTitle: 'Upload a document',
      type: 'Document type',
      file: 'File',
      fileHint: '{{extensions}}, up to {{max}} MB.',
      share: 'Show this document to the applicant',
      shareHint: 'This cannot be changed after the upload.',
      fileMissing: 'Choose a file.',
      fileType: 'Choose a {{extensions}} file.',
      fileEmpty: 'The file is empty.',
      fileTooLarge: 'The file is larger than {{max}} MB.',
      uploading: 'Uploading… {{percent}}%',
      finalizing: 'Checking the upload…',
      download: 'Download',
      downloadLabel: 'Download {{name}}',
      added: 'Document added',
      rejected: 'The document was rejected ({{reason}}).',
    },
    department: {
      listTitle: 'Cases referred to your department',
      listSubtitle: 'Planning cases whose referrals your department answers.',
      empty: 'No case is referred to your department.',
      detailNote:
        "You can see this case because it was referred to your department. Answer your department's referrals below.",
    },
    issues: {
      unknownKey: 'A value this action does not take was sent',
      reasons: {
        missing: 'required',
        type: 'not a valid value',
        range: 'outside the allowed range',
        too_long: 'too long',
        duplicate: 'given twice',
        unknown_code: 'not in the list',
        unknown_condition: "not one of the case's conditions",
        future: 'in the future',
        too_old: 'more than 30 days back',
        before_previous_event: "before the case's last recorded step",
        unknown_key: 'not taken by this action',
      },
    },
    keys: {
      agencies: 'Agencies',
      agency: 'Agency',
      referralId: 'Referral',
      outcome: 'Answer',
      answeredOn: 'Date of the answer',
      conditions: 'Conditions',
      targetCaseId: 'Linked case',
      external: 'Outside reference',
      relation: 'Link',
      linkId: 'Link',
      windowDays: 'Comment window',
      kind: 'Kind',
      reason: 'Reason',
    },
  },
  guards: {
    separation_of_duties_required: 'the person who wrote the recommendation or submitted last must not decide',
    referrals_settled: 'some referrals of this round are still unanswered',
    check_latest_failed_overridable: 'the latest failed check cannot be accepted with exceptions',
    check_latest_passed_or_accepted: 'the latest check has not passed and was not accepted',
    input_date_not_after_clock_due: 'the date must not be after the deadline',
    link_present: 'the case is not linked to its planning permission yet',
    link_parent_valid: 'the linked planning permission is not granted or no longer valid',
    constraints_determined: 'an officer must first decide on the constraint findings',
    rules_determined: 'an officer must first decide on the rule results',
  },
  errors: {
    input_invalid: 'Some of the values were refused. Correct the fields marked below and try again.',
    condition_not_met:
      'This action no longer applies to the case as it stands. Reload the page to see what is possible now.',
    decision_number_taken: 'That decision number is already used by another case. Enter another number.',
    no_referrals: "This case's procedure has no referrals.",
    referral_not_found: 'That referral is not on this case, or it is not yours to answer.',
    referral_exists: 'That agency already has an open referral on this case.',
    referral_closed: 'That referral is already answered or withdrawn.',
    referral_not_due: 'The comment window has not passed yet, so no deemed answer can be recorded.',
    relation_unknown: "This case's procedure does not allow that link.",
    link_exists: 'This case is already linked for that purpose. Unlink it first.',
    link_not_found: 'That link is no longer on this case.',
    referral_requests_closed: 'New referrals are closed: the committee has decided this case.',
    referral_window_unknown: 'Circulate the case first: a referral takes its comment window from the circulation.',
    link_target_not_found: 'That case was not found, or you cannot see it.',
    link_kind_mismatch: 'A case links only to a parent of the kind the link names, never to one of its own kind.',
  },
  events: {
    referral_requested: 'Referral sent',
    referral_answered: 'Referral answered',
    referral_withdrawn: 'Referral withdrawn',
    case_linked: 'Case linked',
    case_unlinked: 'Case unlinked',
    check_exceptions_accepted: 'Drawing check accepted',
  },
} as const
